import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HumanContentDialogProps {
  open: boolean;
  onConfirmNoHuman: () => void;
  onConfirmHasHuman: (resourceName: string) => void;
  onCancel: () => void;
}

const HumanContentDialog: React.FC<HumanContentDialogProps> = ({
  open,
  onConfirmNoHuman,
  onConfirmHasHuman,
  onCancel,
}) => {
  const { t } = useTranslation('common');
  const [resourceName, setResourceName] = useState('');

  if (!open) return null;

  const handleHasHuman = () => {
    if (!resourceName.trim()) return;
    onConfirmHasHuman(resourceName.trim());
    setResourceName('');
  };

  const handleNoHuman = () => {
    setResourceName('');
    onConfirmNoHuman();
  };

  const handleCancel = () => {
    setResourceName('');
    onCancel();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.5)',
    }} onClick={handleCancel}>
      <div style={{
        background: 'var(--surface)', borderRadius: 12, padding: '24px 28px',
        width: 440, maxWidth: '90vw', boxShadow: 'var(--shadow-lg)', color: 'var(--text)',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1.05rem' }}>{t('humanContent.title')}</h3>
        <p style={{ margin: '0 0 20px', color: 'var(--text-2)', fontSize: '0.9rem' }}>
          {t('humanContent.confirmType')}
        </p>

        <div style={{
          border: '1px solid var(--border)', borderRadius: 8,
          padding: '16px', marginBottom: 12,
          background: 'var(--surface-2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: '1.1rem' }}>🎭</span>
            <strong style={{ fontSize: '0.95rem' }}>{t('humanContent.hasHumanTitle')}</strong>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: 'var(--text-3)' }}>
            {t('humanContent.hasHumanDesc')}
          </p>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-2)', display: 'block', marginBottom: 4 }}>
              {t('humanContent.resourceNameLabel')}
            </label>
            <input
              type="text"
              value={resourceName}
              onChange={e => setResourceName(e.target.value)}
              placeholder={t('humanContent.resourceNamePlaceholder')}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 6,
                border: '1px solid var(--border)', fontSize: '0.88rem',
                boxSizing: 'border-box', background: 'var(--surface, #fff)', color: 'var(--text)',
              }}
            />
          </div>
          <button
            onClick={handleHasHuman}
            disabled={!resourceName.trim()}
            style={{
              width: '100%', padding: '8px 0', borderRadius: 6,
              background: resourceName.trim() ? 'var(--primary)' : 'var(--btn-secondary-bg, #ccc)',
              color: '#fff', border: 'none', cursor: resourceName.trim() ? 'pointer' : 'not-allowed',
              fontSize: '0.88rem', fontWeight: 500,
            }}
          >
            {t('humanContent.submitForReview')}
          </button>
        </div>

        <div style={{
          border: '1px solid var(--border)', borderRadius: 8,
          padding: '16px',
          background: 'var(--surface-2)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: '1.1rem' }}>📁</span>
            <strong style={{ fontSize: '0.95rem' }}>{t('humanContent.noHumanTitle')}</strong>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.82rem', color: 'var(--text-3)' }}>
            {t('humanContent.noHumanDesc')}
          </p>
          <button
            onClick={handleNoHuman}
            style={{
              width: '100%', padding: '8px 0', borderRadius: 6,
              background: 'var(--btn-secondary-bg, #f0f0f0)', color: 'var(--text)',
              border: '1px solid var(--border)', cursor: 'pointer',
              fontSize: '0.88rem', fontWeight: 500,
            }}
          >
            {t('humanContent.directUpload')}
          </button>
        </div>

        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '6px 16px', borderRadius: 6, border: '1px solid var(--border)',
              background: 'transparent', cursor: 'pointer', fontSize: '0.85rem',
              color: 'var(--text-3)',
            }}
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HumanContentDialog;
